import { TachographDataFile } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_TACHOGRAPH_DATA_FILE } from "@/constants/resultsLimits";
import { chunkArray } from "@/utilities/chunkArray";

export function settleFileChunks(
  results: PromiseSettledResult<TachographDataFile[]>[],
  limit: number,
): { files: TachographDataFile[]; hadPartialFailure: boolean; truncated: boolean } {
  const files: TachographDataFile[] = [];
  let hadPartialFailure = false;
  let truncated = false;

  for (const result of results) {
    if (result.status === "fulfilled") {
      if (result.value.length >= limit) {
        truncated = true;
      }
      files.push(...result.value);
    } else {
      hadPartialFailure = true;
    }
  }

  if (results.length > 0 && results.every(result => result.status === "rejected")) {
    const first = results[0] as PromiseRejectedResult;
    throw first.reason instanceof Error ? first.reason : new Error("Tachograph file request failed");
  }

  return { files, hadPartialFailure, truncated };
}

/**
 * Fetches the files for `ids`. A page that comes back full (`limit` rows, the cap Geotab clamps
 * to) may hide more rows, so the ids are split in half and each half re-requested, recursively,
 * down to a single id. A single id that still fills a page is returned as is, and
 * {@link settleFileChunks} flags it as truncated. A failing request is returned as a rejected
 * result, so one failing half does not take the other half with it.
 *
 * Halves are disjoint id sets, so no file is returned twice.
 */
export async function fetchSplittingFullPages(
  ids: string[],
  fetchChunk: (ids: string[]) => Promise<TachographDataFile[]>,
  limit: number,
): Promise<PromiseSettledResult<TachographDataFile[]>[]> {
  let page: TachographDataFile[];
  try {
    page = await fetchChunk(ids);
  } catch (reason) {
    return [{ status: "rejected", reason }];
  }

  if (page.length < limit || ids.length <= 1) {
    return [{ status: "fulfilled", value: page }];
  }

  const middle = Math.ceil(ids.length / 2);
  const halves = await Promise.all([
    fetchSplittingFullPages(ids.slice(0, middle), fetchChunk, limit),
    fetchSplittingFullPages(ids.slice(middle), fetchChunk, limit),
  ]);
  return halves.flat();
}

/**
 * The API takes the ids as a list, so long fleets are asked for in chunks of this many ids. A
 * chunk can still exceed the 5000-row cap (weekly readouts over twelve months add up to many files
 * per vehicle), so a chunk that comes back full is re-requested in halves by
 * {@link fetchSplittingFullPages}; "latest file" and the overdue statuses are computed from
 * complete data unless a single id alone fills a page.
 */
const ID_CHUNK_SIZE = 500;

/** Readouts change slowly, so a returning tab does not need to re-run the twelve-month reads. */
const FILES_STALE_TIME_MS = 60 * 60 * 1000;

const DEFAULT_MONTHS_BACK = 12;

/**
 * The tachograph readouts for the given vehicles. Binary content is left out —
 * it is only fetched for the single file a user actually downloads.
 *
 * The window is `fromTimestamp`: `fromActivityTimestampEnd` cannot be combined with any other
 * criterion ("If FromActivityTimestampEnd is specified all other search criteria should be left
 * default"), so together with the device filter it fails the whole request.
 */
export function useTachographDeviceFiles(
  deviceIds: string[],
  monthsBack = DEFAULT_MONTHS_BACK,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["tachographDeviceFiles", activeSessionId, deviceIds, monthsBack],

    queryFn: async ({ signal }) => {
      const since = new Date();
      since.setMonth(since.getMonth() - monthsBack);

      const fetchChunk = (ids: string[]) =>
        geotabClient.get(
          "TachographDataFile",
          {
            deviceSearch: { deviceIds: ids },
            type: "Device",
            fromTimestamp: since,
            includeBinaryData: false,
          },
          RESULTS_LIMIT_TACHOGRAPH_DATA_FILE,
          signal,
        );

      const deviceResults = (
        await Promise.all(
          chunkArray(deviceIds, ID_CHUNK_SIZE).map(ids =>
            fetchSplittingFullPages(ids, fetchChunk, RESULTS_LIMIT_TACHOGRAPH_DATA_FILE),
          ),
        )
      ).flat();

      return settleFileChunks(deviceResults, RESULTS_LIMIT_TACHOGRAPH_DATA_FILE);
    },

    enabled: deviceIds.length > 0,
    staleTime: FILES_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * The tachograph readouts for the given drivers. Binary content is left out —
 * it is only fetched for the single file a user actually downloads.
 */
export function useTachographDriverFiles(
  driverIds: string[],
  monthsBack = DEFAULT_MONTHS_BACK,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["tachographDriverFiles", activeSessionId, driverIds, monthsBack],

    queryFn: async ({ signal }) => {
      const since = new Date();
      since.setMonth(since.getMonth() - monthsBack);

      const fetchChunk = (ids: string[]) =>
        geotabClient.get(
          "TachographDataFile",
          {
            userSearch: { userIds: ids },
            type: "Driver",
            fromTimestamp: since,
            includeBinaryData: false,
          },
          RESULTS_LIMIT_TACHOGRAPH_DATA_FILE,
          signal,
        );

      const driverResults = (
        await Promise.all(
          chunkArray(driverIds, ID_CHUNK_SIZE).map(ids =>
            fetchSplittingFullPages(ids, fetchChunk, RESULTS_LIMIT_TACHOGRAPH_DATA_FILE),
          ),
        )
      ).flat();

      return settleFileChunks(driverResults, RESULTS_LIMIT_TACHOGRAPH_DATA_FILE);
    },

    enabled: driverIds.length > 0,
    staleTime: FILES_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}
